import { Guid } from "client_legacy/ndaw/guid";
import {
	InterestedParty,
	LiveInformationProvider,
	SuccessfulCacheItem
} from "client_legacy/Workspace/LiveInformation/liveInformationProvider";
import {
	AfasLiveInformationResponseModel,
	AfasResourceKind,
	AfasLiveInformationRequestModel,
	getLiveInformation,
	AfasResultStatus,
	AfasConnectionInterests,
	AfasListItem
} from "client/Api/Workspace/LiveInformation/Afas/afasLiveInformation";
import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { NormalizedMap } from "client_legacy/ndaw/normalizedMap";

interface AfasLiveInformationProviderOptions {
	intervalInSeconds: number;
}

interface AfasResourceId {
	connectionId: number;
	resourceKind: AfasResourceKind;
}

interface InterestedAfasPartyForResourceKind<TResourceKind extends AfasResourceKind> extends InterestedParty<Guid, AfasResourceId> {
	resourceKind: TResourceKind;
	updateItems(items: LazyLoadPageResult<AfasListItem<TResourceKind>>): void;
	informAboutAccessViolation(): void;
	informAboutNewTokenRequirement(): void;
}

type InterestedAfasParty = InterestedAfasPartyForResourceKind<AfasResourceKind.MyTasks>
	| InterestedAfasPartyForResourceKind<AfasResourceKind.MySignals>;

class AfasLiveInformationProvider extends LiveInformationProvider<
	AfasLiveInformationProviderOptions,
	Guid,
	AfasResourceId,
	AfasConnectionInterests,
	InterestedAfasParty,
	AfasLiveInformationResponseModel
> {

	constructor(options: AfasLiveInformationProviderOptions) {
		super(options);
	}

	protected async getDataFromBackend(
		entries: ReadonlyArray<[AfasResourceId, AfasConnectionInterests]>
	): Promise<ReadonlyArray<[AfasResourceId, AfasLiveInformationResponseModel]>> {
		const requests: AfasLiveInformationRequestModel[] = entries.map(entry => this.convertResourceIdToRequest(entry[0], entry[1]));
		const responses: AfasLiveInformationResponseModel[] = await getLiveInformation(requests);

		return responses.map(response => [this.convertResponseToResourceId(response), response]);
	}

	protected updateCacheItem(
		cacheItem: SuccessfulCacheItem<AfasConnectionInterests, AfasLiveInformationResponseModel>,
		response: [AfasConnectionInterests, AfasLiveInformationResponseModel]
	) {
		const data: AfasLiveInformationResponseModel = response[1];

		cacheItem.retrievedInterests.items = true;
		cacheItem.data = data;
	}

	protected informParty(party: InterestedAfasParty, cacheItem: [AfasConnectionInterests, AfasLiveInformationResponseModel]) {
		const response: AfasLiveInformationResponseModel = cacheItem[1];

		switch (response.status) {
			case AfasResultStatus.Success:
				switch (response.resourceKind) {
					case AfasResourceKind.MyTasks:
						this.ensureResourceKind(party, AfasResourceKind.MyTasks)
							.updateItems(response.items);
						break;
					case AfasResourceKind.MySignals:
						this.ensureResourceKind(party, AfasResourceKind.MySignals)
							.updateItems(response.items);
						break;
					default:
						throw new UnreachableCaseError(response);
				}
				break;
			case AfasResultStatus.NoAccess:
				party.informAboutAccessViolation();
				break;
			case AfasResultStatus.RequireNewToken:
				party.informAboutNewTokenRequirement();
				break;
			case AfasResultStatus.GeneralFailure:
				party.informAboutGlobalRetrievalFailure();
				break;
			default:
				throw new UnreachableCaseError(response);
		}
	}

	protected override createResourceMap<TValue>(): Map<AfasResourceId, TValue> {
		return new NormalizedMap<AfasResourceId, TValue>(normalizeKey);
	}

	private ensureResourceKind<TResourceKind extends AfasResourceKind>(party: InterestedAfasParty, resourceKind: TResourceKind)
		: InterestedAfasPartyForResourceKind<TResourceKind> {
		if (party.resourceKind !== resourceKind) {
			throw new Error("Afas response has different resource type then party that requested it.");
		}

		return party as InterestedAfasPartyForResourceKind<TResourceKind>;
	}

	private convertResourceIdToRequest(resourceId: AfasResourceId, interestedIn: AfasConnectionInterests): AfasLiveInformationRequestModel {
		return {
			connectionId: resourceId.connectionId,
			resourceKind: resourceId.resourceKind,
			interestedIn: interestedIn
		};
	}

	private convertResponseToResourceId(response: AfasLiveInformationResponseModel): AfasResourceId {
		return {
			connectionId: response.connectionId,
			resourceKind: response.resourceKind
		};
	}
}

function normalizeKey(key: AfasResourceId): string {
	let normalizedKey: string = `connection_${key.connectionId}`;
	switch (key.resourceKind) {
		case AfasResourceKind.MyTasks:
			normalizedKey += "_MyTasks";
			break;
		case AfasResourceKind.MySignals:
			normalizedKey += "_MySignals";
			break;
		default:
			throw new UnreachableCaseError(key.resourceKind);
	}

	return normalizedKey;
}

export {
	AfasLiveInformationProvider,
	type InterestedAfasPartyForResourceKind,
	type InterestedAfasParty,
	type AfasResourceId
};